import { useEffect, RefObject } from "react";
import { FormInstance } from "@ir/ui";
import { FilterInfo } from "@/store/deduction/deductionFilter";
import { ApprovalFilterInfo } from "@/store/approvals";
import { AnalyticsFilterInfo } from "@/store/analytics";

type AppFilterInfo = FilterInfo | ApprovalFilterInfo | AnalyticsFilterInfo;

interface UseDefaultFilterSyncProps {
  form: FormInstance;
  filterInfo: AppFilterInfo;
  defaultFilterInfo: AppFilterInfo;
  isFilterApplied: boolean;
  setFilter: (filter: AppFilterInfo) => void;
  /** When true, skip the next sync (e.g. user just selected a date preset in the form). Prevents reverting their selection. */
  skipNextSyncRef?: RefObject<boolean>;
  /** When false, skip sync entirely — prevents overwriting localStorage before hydration from storage completes. */
  isHydrated?: boolean;
}

/**
 * Ensures filterInfo matches default values when filter is not applied
 */
export const useDefaultFilterSync = ({
  form,
  filterInfo,
  defaultFilterInfo,
  isFilterApplied,
  setFilter,
  skipNextSyncRef,
  isHydrated,
}: UseDefaultFilterSyncProps) => {
  useEffect(() => {
    if (isHydrated === false) {
      return;
    }

    if (isFilterApplied) {
      return;
    }

    if (skipNextSyncRef?.current) {
      skipNextSyncRef.current = false;
      return;
    }

    const matchesDefault =
      filterInfo.checkFromDate === defaultFilterInfo.checkFromDate &&
      filterInfo.checkToDate === defaultFilterInfo.checkToDate &&
      filterInfo.checkDateRange === defaultFilterInfo.checkDateRange;

    if (!matchesDefault) {
      const updatedFilterInfo: AppFilterInfo = {
        ...(filterInfo as FilterInfo),
        checkFromDate: defaultFilterInfo.checkFromDate,
        checkToDate: defaultFilterInfo.checkToDate,
        checkDateRange: defaultFilterInfo.checkDateRange,
      };
      setFilter(updatedFilterInfo);
      form.setFieldsValue({
        ...form.getFieldsValue(),
        checkFromDate: defaultFilterInfo.checkFromDate,
        checkToDate: defaultFilterInfo.checkToDate,
        checkDateRange: defaultFilterInfo.checkDateRange,
      });
    }
  }, [isHydrated, filterInfo, defaultFilterInfo, isFilterApplied, setFilter, form, skipNextSyncRef]);
};

